import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Context Store - Manages active tenant, domain, and subject context
 * This provides the context headers that should be sent with all API requests
 * based on the user's current role and navigation state.
 */
interface ContextStore {
  // Active context IDs
  activeTenantId: string | null;
  activeDomainId: string | null;
  activeSubjectId: string | null;

  // Actions
  setActiveTenantId: (tenantId: string | null) => void;
  setActiveDomainId: (domainId: string | null) => void;
  setActiveSubjectId: (subjectId: string | null) => void;

  // Set full context at once
  setActiveContext: (context: {
    tenantId?: string | null;
    domainId?: string | null;
    subjectId?: string | null;
  }) => void;

  // Clear context
  clearActiveContext: () => void;
  clearDomainAndSubject: () => void;
  clearSubject: () => void;

  // Get context headers for API requests
  getContextHeaders: () => Record<string, string>;
}

export const useContextStore = create<ContextStore>()(
  persist(
    (set, get) => ({
      activeTenantId: null,
      activeDomainId: null,
      activeSubjectId: null,

      setActiveTenantId: (tenantId) => {
        set({ activeTenantId: tenantId });
        // Clear domain and subject when tenant changes
        if (tenantId !== get().activeTenantId) {
          set({ activeDomainId: null, activeSubjectId: null });
        }
      },

      setActiveDomainId: (domainId) => {
        set({ activeDomainId: domainId });
        // Clear subject when domain changes
        if (domainId !== get().activeDomainId) {
          set({ activeSubjectId: null });
        }
      },

      setActiveSubjectId: (subjectId) => {
        set({ activeSubjectId: subjectId });
      },

      setActiveContext: (context) => {
        const updates: Partial<
          Pick<ContextStore, 'activeTenantId' | 'activeDomainId' | 'activeSubjectId'>
        > = {};

        if (context.tenantId !== undefined) {
          updates.activeTenantId = context.tenantId;
        }
        if (context.domainId !== undefined) {
          updates.activeDomainId = context.domainId;
        }
        if (context.subjectId !== undefined) {
          updates.activeSubjectId = context.subjectId;
        }

        set(updates);
      },

      clearActiveContext: () => {
        set({
          activeTenantId: null,
          activeDomainId: null,
          activeSubjectId: null,
        });
      },

      clearDomainAndSubject: () => {
        set({
          activeDomainId: null,
          activeSubjectId: null,
        });
      },

      clearSubject: () => {
        set({ activeSubjectId: null });
      },

      getContextHeaders: () => {
        const state = get();
        const headers: Record<string, string> = {};

        if (state.activeTenantId) {
          headers['X-Active-Tenant-Id'] = state.activeTenantId;
        }
        if (state.activeDomainId) {
          headers['X-Active-Domain-Id'] = state.activeDomainId;
        }
        if (state.activeSubjectId) {
          headers['X-Active-Subject-Id'] = state.activeSubjectId;
        }

        return headers;
      },
    }),
    {
      name: 'context-storage',
      partialize: (state) => ({
        activeTenantId: state.activeTenantId,
        activeDomainId: state.activeDomainId,
        activeSubjectId: state.activeSubjectId,
      }),
    }
  )
);

// Helper functions to get individual context IDs
export const getActiveTenantId = () => {
  return useContextStore.getState().activeTenantId;
};

export const getActiveDomainId = () => {
  return useContextStore.getState().activeDomainId;
};

export const getActiveSubjectId = () => {
  return useContextStore.getState().activeSubjectId;
};

export const getContextHeaders = () => {
  return useContextStore.getState().getContextHeaders();
};

// Helper to sync context from auth store (to be called when user logs in or switches tenant)
export const syncContextFromAuth = (currentTenantId?: string | null) => {
  const contextStore = useContextStore.getState();

  // If tenant changed, update and clear lower levels
  if (currentTenantId !== contextStore.activeTenantId) {
    contextStore.setActiveTenantId(currentTenantId || null);
  }
};

// Debug function to clear localStorage and reset context
export const debugClearContextStorage = () => {
  localStorage.removeItem('context-storage');
  const contextStore = useContextStore.getState();
  contextStore.clearActiveContext();
};

// Enhanced debug function to diagnose storage issues
export const debugInspectAllStorage = () => {
  // Context Store
  const contextState = useContextStore.getState();

  // localStorage inspection
  const localKeys = ['auth-store', 'context-storage', 'lecturo_storage_version'];

  // sessionStorage inspection
  const sessionKeys = ['lecturo_current_role_context'];

  return {
    contextState,
    localStorage: Object.fromEntries(localKeys.map((key) => [key, localStorage.getItem(key)])),
    sessionStorage: Object.fromEntries(
      sessionKeys.map((key) => [key, sessionStorage.getItem(key)])
    ),
  };
};

// Debug function to fix professor "no subjects" issue
export const debugFixProfessorSubjects = () => {
  // Step 1: Clear all storage
  localStorage.removeItem('auth-store');
  localStorage.removeItem('context-storage');
  sessionStorage.removeItem('lecturo_current_role_context');

  // Step 2: Clear context store
  const contextStore = useContextStore.getState();
  contextStore.clearActiveContext();
};

// Expose debug functions to window in development
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).debugClearContextStorage = debugClearContextStorage;
  (window as any).debugInspectAllStorage = debugInspectAllStorage;
  (window as any).debugFixProfessorSubjects = debugFixProfessorSubjects;
  (window as any).getContextStore = () => useContextStore.getState();
}
